import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Stack,
  Pagination,
  TableSortLabel,
  useTheme
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useListReceiptsByStore } from "../../hooks/api/useReceipt";
import NoReceiptFound from "./NoReceiptFound";
import ReceiptDetailModal from "./ReceiptDetailModal";
import { Receipt, ReceiptStatus } from "./types/receipt";
import { PaymentMethodLabels } from "../Payment/enums/paymentMethod";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  BRAND_COLOR,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { formatDate } from "../Orders/utils/orderUtils";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import ListPageToolbar from "../common/ListPageToolbar";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { useReportError } from "../../hooks/useReportError";
import { Chip } from "@mui/material";

type SortOrder = "asc" | "desc";

type SortableReceiptKey = keyof Pick<
  Receipt,
  "amount" | "paymentMethod" | "paymentDate" | "status" | "createdAt"
>;

/** API uses `receiptId`; some fixtures use `id`. */
const receiptRowId = (r: Receipt & { id?: string }) =>
  r.receiptId || r.id || "";

const ReceiptList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const formatCurrency = useFormatCurrency();

  const { receiptList } = useSelector((state: RootState) => state.receipts);
  const listReceiptsByStore = useListReceiptsByStore();
  const reportError = useReportError();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const [fetchDone, setFetchDone] = useState(false);

  const [search, setSearch] = useState("");
  const [detailReceiptId, setDetailReceiptId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [orderBy, setOrderBy] = useState<SortableReceiptKey>("createdAt");
  const itemsPerPage = 10;

  useEffect(() => {
    (async () => {
      try {
        await listReceiptsByStore.mutateAsync();
      } catch (err) {
        reportError("Failed to fetch receipt list (UI)", err, {
          component: "ReceiptList"
        });
      } finally {
        setFetchDone(true);
      }
    })();
  }, [reportError]);

  const getStatusColor = (
    status: ReceiptStatus
  ): "success" | "error" | "default" => {
    switch (status) {
      case ReceiptStatus.ACTIVE:
        return "success";
      case ReceiptStatus.VOID:
        return "error";
      default:
        return "default";
    }
  };

  const filteredList = useMemo(() => {
    if (!search.trim()) return receiptList;
    const q = search.toLowerCase();
    return receiptList.filter((receipt) => {
      const inv = (receipt.invoiceNumber ?? "").toLowerCase();
      const ord = (receipt.orderNumber ?? "").toLowerCase();
      return (
        inv.includes(q) ||
        ord.includes(q) ||
        receipt.invoiceId.toLowerCase().includes(q) ||
        receipt.paymentId.toLowerCase().includes(q) ||
        (PaymentMethodLabels[receipt.paymentMethod] ?? receipt.paymentMethod)
          .toLowerCase()
          .includes(q)
      );
    });
  }, [receiptList, search]);

  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      const aValue = a[orderBy] ?? "";
      const bValue = b[orderBy] ?? "";
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (orderBy === "createdAt" || orderBy === "paymentDate") {
        const aDate = new Date(String(aValue)).getTime();
        const bDate = new Date(String(bValue)).getTime();
        return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
      }
      return sortOrder === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredList, sortOrder, orderBy]);

  const paginatedList = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return sortedList.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedList, page]);

  const handleSort = (property: SortableReceiptKey) => {
    const isAsc = orderBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const paymentDateDisplay = (receipt: Receipt & { issuedDate?: string }) => {
    const raw = receipt.paymentDate ?? receipt.issuedDate;
    return raw ? formatDate(raw) : "—";
  };

  if (!fetchDone) {
    return (
      <CommonLoader
        loadingMessage={t("receipt-list-loading", "Loading receipts...")}
      />
    );
  }

  if (receiptList.length === 0) {
    return <NoReceiptFound />;
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 3
      }}
    >
      <ListPageToolbar
        title={t("receipts", "Receipts")}
        icon={
          <ReceiptLongOutlinedIcon sx={{ color: BRAND_COLOR, fontSize: 22 }} />
        }
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t(
          "receipt-search-placeholder",
          "Search by invoice number, order number, payment id, or payment method."
        )}
        isMobile={matchMD}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "amount"}
                  direction={orderBy === "amount" ? sortOrder : "asc"}
                  onClick={() => handleSort("amount")}
                >
                  {t("amount", "Amount")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "paymentMethod"}
                  direction={orderBy === "paymentMethod" ? sortOrder : "asc"}
                  onClick={() => handleSort("paymentMethod")}
                >
                  {t("payment-method", "Payment Method")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "paymentDate"}
                  direction={orderBy === "paymentDate" ? sortOrder : "asc"}
                  onClick={() => handleSort("paymentDate")}
                >
                  {t("payment-date", "Payment Date")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? sortOrder : "asc"}
                  onClick={() => handleSort("status")}
                >
                  {t("status", "Status")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? sortOrder : "asc"}
                  onClick={() => handleSort("createdAt")}
                >
                  {t("created", "Created")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">{t("actions", "Actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedList.map((receipt) => {
              const rowId = receiptRowId(receipt as Receipt & { id?: string });
              return (
                <TableRow key={rowId} hover>
                  <TableCell sx={{ fontSize: 13 }}>
                    {formatCurrency(
                      receipt.amount,
                      (receipt as Receipt & { currency?: string }).currency
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {PaymentMethodLabels[receipt.paymentMethod] ||
                      receipt.paymentMethod}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {paymentDateDisplay(
                      receipt as Receipt & { issuedDate?: string }
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={receipt.status}
                      color={getStatusColor(receipt.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {formatDate(receipt.createdAt)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: 13 }}>
                    <IconButton
                      color="primary"
                      aria-label={t("view-receipt", "View receipt")}
                      onClick={() => {
                        if (rowId) setDetailReceiptId(rowId);
                      }}
                    >
                      <VisibilityOutlinedIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedList.length > itemsPerPage && (
        <Stack spacing={2} alignItems="center" mt={3}>
          <Pagination
            count={Math.ceil(sortedList.length / itemsPerPage)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Stack>
      )}

      <ReceiptDetailModal
        open={detailReceiptId !== null}
        receiptId={detailReceiptId}
        onClose={() => setDetailReceiptId(null)}
      />
    </Box>
  );
};

export default ReceiptList;
